import { useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import OfferCard from "@/components/base/OfferCard";
import ArticleHero from "./components/ArticleHero";
import ArticleContent from "./components/ArticleContent";
import ArticleFAQ from "./components/ArticleFAQ";
import ArticleSidebar from "./components/ArticleSidebar";
import ArticleAdSlot from "./components/ArticleAdSlot";
import { renderArticleRichText } from "./components/renderArticleRichText";
import { useArticleCatalog } from "@/hooks/useArticleCatalog";
import { useAdminDeals, useAdminProducts } from "@/hooks/useAdminStore";
import { formatCanonicalDateForStructuredData } from "@/utils/date";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { buildCanonicalUrl, usePageSeo } from "@/hooks/usePageSeo";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const { articles } = useArticleCatalog();
  const { products } = useAdminProducts();
  const { deals } = useAdminDeals();
  const { settings } = useSiteSettings();
  const article = articles.find((item) => item.slug === (slug ?? "")) ?? articles[0];

  const heroImage = article.heroImage || article.image;

  usePageSeo({
    title: article.metaTitle?.trim() || `${article.title} | ${settings.siteName}`,
    description: article.metaDescription?.trim() || article.excerpt,
    canonicalPath: `/artigo/${article.slug}`,
    keywords: article.tags.join(", ") || settings.seoKeywords,
    image: heroImage,
    siteName: settings.siteName,
    ogType: "article",
  });

  const relatedArticles = useMemo(
    () =>
      article.relatedArticleSlugs
        .map((relatedSlug) => articles.find((candidate) => candidate.slug === relatedSlug))
        .filter(Boolean)
        .map((relatedArticle) => ({
          slug: relatedArticle!.slug,
          title: relatedArticle!.title,
          image: relatedArticle!.image,
          readTime: relatedArticle!.readTime,
          category: relatedArticle!.category,
        })),
    [article.relatedArticleSlugs, articles]
  );

  const relatedProducts = useMemo(() => {
    const catalog = Array.from(
      new Map(
        [...products, ...deals].map((item) => [item.slug, item])
      ).values()
    );

    return catalog
      .filter((item) => {
        const categorySlug = item.categorySlug ?? "";
        const subcategorySlug = "subcategorySlug" in item ? item.subcategorySlug ?? "" : "";
        return categorySlug === article.categorySlug || subcategorySlug === article.categorySlug;
      })
      .slice(0, 4);
  }, [article.categorySlug, deals, products]);

  const tableOfContents = useMemo(
    () => [
      { id: "resumo", label: "Resumo Rápido" },
      ...article.sections
        .filter((section) => section.title)
        .map((section) => ({ id: section.id, label: section.title! })),
      { id: "faq", label: "Perguntas Frequentes" },
    ],
    [article.sections]
  );

  const articleUrl = buildCanonicalUrl(`/artigo/${article.slug}`);
  const categoryUrl = buildCanonicalUrl(`/categoria/${article.categorySlug}`);

  const articleSchema = {

    "@context": "https://schema.org",
    "@type": "Article",
    mainEntityOfPage: articleUrl,
    url: articleUrl,
    headline: article.title,
    description: article.metaDescription?.trim() || article.excerpt,
    image: [heroImage],
    author: {
      "@type": "Person",
      name: article.author,
    },
    publisher: {
      "@type": "Organization",
      name: settings.siteName,
      logo: article.authorAvatar || settings.logoUrl,
    },
    datePublished: formatCanonicalDateForStructuredData(article.date),
    dateModified: formatCanonicalDateForStructuredData(article.updatedDate),
    articleSection: article.category,
    keywords: article.tags.join(", "),
    inLanguage: "pt-BR",
    isAccessibleForFree: true,
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: buildCanonicalUrl("/") },
      { "@type": "ListItem", position: 2, name: article.category, item: categoryUrl },
      { "@type": "ListItem", position: 3, name: article.title, item: articleUrl },
    ],
  };

  return (
    <div className="min-h-screen bg-white">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />

      <Navbar />

      <ArticleHero
        title={article.title}
        excerpt={article.excerpt}
        category={article.category}
        categorySlug={article.categorySlug}
        type={article.type}
        heroImage={heroImage}
        author={article.author}
        authorAvatar={article.authorAvatar}
        date={article.date}
        updatedDate={article.updatedDate}
        readTime={article.readTime}
        views={article.views}
        tags={article.tags}
      />

      <div className="border-b border-gray-100 bg-gray-50">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 md:px-6">
          <nav className="min-w-0 flex-1 items-center gap-2 text-xs text-gray-500 flex">
            <Link to="/" className="transition-colors hover:text-orange-500">Home</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to={`/categoria/${article.categorySlug}`} className="transition-colors hover:text-orange-500">
              {article.category}
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="line-clamp-1 font-medium text-gray-700">{article.title}</span>
          </nav>
          <Link
            to="/guias"
            className="hidden items-center gap-2 rounded-full border border-sky-100 bg-sky-50 px-4 py-2 text-sm font-semibold text-sky-800 transition-colors hover:bg-sky-100 md:inline-flex"
          >
            Mais Artigos
            <i className="ri-arrow-right-line text-sky-500"></i>
          </Link>

        </div>
      </div>

      <main className="mx-auto max-w-7xl px-4 pb-16 pt-8 md:px-6">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_300px]">
          <div>
            <p className="mb-4 text-xl font-normal text-gray-900 md:text-2xl">
              {article.title}
            </p>
            <div id="resumo" className="scroll-mt-24 mb-8 rounded-2xl border border-sky-100 bg-sky-50 p-5">
              <div className="mb-3 flex items-center gap-2">
                <div className="flex h-6 w-6 items-center justify-center">
                  <i className="ri-lightbulb-line text-sky-500"></i>
                </div>
                <span className="text-sm font-bold text-gray-900">Resumo Rápido</span>
              </div>

              <div className="space-y-2 text-gray-900">
                {renderArticleRichText(article.quickSummary, {
                  paragraphClassName: "text-sm leading-relaxed text-gray-900",
                  bulletItemClassName: "text-sm leading-relaxed text-gray-900",
                  standaloneBoldClassName: "mt-4 mb-1 text-sm font-bold text-gray-900",
                })}
              </div>
            </div>

            <div className="mb-8">
              <ArticleAdSlot
                slotKey="articleTopBanner"
                minHeightClassName="min-h-[290px] max-w-[720px] mx-auto"
                scrollTriggerPercent={0}
              />
            </div>

            <ArticleContent sections={article.sections} />

            <ArticleFAQ faqs={article.faqs} articleTitle={article.title} />

            {relatedProducts.length > 0 && (
              <section className="mt-12">
                <div className="mb-5 flex items-center justify-between gap-4">
                  <div>
                    <h2 className="text-xl font-normal text-gray-900 md:text-2xl">
                      Produtos relacionados a {article.category}
                    </h2>
                    <p className="mt-1 text-sm text-gray-500">
                      Continue a navegação pelos itens mais relevantes desta categoria.
                    </p>
                  </div>
                  <Link
                    to={`/categoria/${article.categorySlug}`}
                    className="hidden items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-4 py-2 text-sm font-semibold text-orange-600 transition-colors hover:bg-orange-100 md:inline-flex"
                  >
                    Ver categoria
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
                <div className="grid grid-cols-1 justify-items-start gap-4 sm:grid-cols-2 xl:grid-cols-4">
                  {relatedProducts.map((product) => (
                    <OfferCard
                      key={product.slug}
                      className="w-full"
                      item={{
                        slug: product.slug,
                        image: product.image,
                        name: product.name,
                        price: product.price,
                        originalPrice: product.originalPrice,
                        discount: product.discount,
                        marketplace: product.marketplace,
                        freeShipping: product.freeShipping,
                        fulfillment: "fulfillment" in product ? product.fulfillment : false,
                        officialStoreName:
                          "officialStoreName" in product
                            ? product.officialStoreName || ("officialStore" in product && product.officialStore ? "Loja Oficial" : "")
                            : "",
                        affiliateLink: product.affiliateLink,
                        isFlash: product.badge === "Oferta Relâmpago",
                        category: product.category,
                        categorySlug: product.categorySlug,
                        subcategory: "subcategory" in product ? product.subcategory : product.category,
                        subcategorySlug: "subcategorySlug" in product ? product.subcategorySlug : product.categorySlug,
                      }}
                    />
                  ))}
                </div>
              </section>
            )}

            <div className="my-10">
              <ArticleAdSlot
                slotKey="articleBottomBanner"
                minHeightClassName="min-h-[90px] max-w-[720px] mx-auto"
                scrollTriggerPercent={0.65}
              />
            </div>

            <div className="mb-4 mt-8 flex justify-end">
              <Link
                to="/guias"
                className="inline-flex items-center gap-2 rounded-full border border-sky-100 bg-sky-50 px-4 py-2 text-sm font-semibold text-sky-800 transition-colors hover:bg-sky-100"
              >
                Mais Artigos
                <i className="ri-arrow-right-line text-sky-500"></i>
              </Link>
            </div>

            <div className="mt-8 flex gap-4 rounded-2xl border border-gray-100 bg-gray-50 p-5">
              <div className="h-14 w-14 flex-shrink-0 overflow-hidden rounded-full">
                <img
                  src={article.authorAvatar}
                  alt={article.author}
                  className="h-full w-full object-cover object-top"
                />
              </div>
              <div>
                <p className="text-sm font-bold text-gray-800">{article.author}</p>
                <p className="mt-1 text-xs leading-relaxed text-gray-500">{article.authorBio}</p>
                <div className="mt-2 flex items-center gap-1">
                  <div className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-shield-check-line text-sm text-green-500"></i>
                  </div>
                  <span className="text-xs font-semibold text-green-600">Conteúdo verificado e atualizado</span>
                </div>
              </div>
            </div>

            <div className="mt-6 rounded-xl border border-gray-100 bg-gray-50 p-4">
              <p className="text-xs leading-relaxed text-gray-500">
                <strong className="text-gray-700">Divulgação:</strong> {article.disclosure}
              </p>
            </div>
          </div>

          <ArticleSidebar
            tableOfContents={tableOfContents}
            relatedArticles={relatedArticles}
          />
        </div>
      </main>

      <Footer />
    </div>
  );
}